import { SWRConfig } from 'swr'
import { useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Filter, X } from 'lucide-react'
import GlobalFilters from '@/components/dashboard/GlobalFilters'
import SummaryCards from '@/components/dashboard/SummaryCards'
import PrefectureRankingChart from '@/components/dashboard/PrefectureRankingChart'
import TeamWinRankingChart from '@/components/dashboard/TeamWinRankingChart'
import GoalTrendChart from '@/components/dashboard/GoalTrendChart'
import ChampionshipMap from '@/components/dashboard/ChampionshipMap'
import MatchesDataTable from '@/components/dashboard/MatchesDataTable'
import DebugInfo from '@/components/dashboard/DebugInfo'
import TopWinRatePrefectures from '@/components/dashboard/TopWinRatePrefectures'
import TopMatchCountPrefectures from '@/components/dashboard/TopMatchCountPrefectures'
import ActiveFiltersBar from '@/components/dashboard/ActiveFiltersBar'
import YouTubeBanner from '@/components/ui/YouTubeBanner'
import useFilterStore from '@/store/useFilterStore'
import { useFilterOptions } from '@/hooks/useFilterOptions'
import { useURLParams } from '@/hooks/useURLParams'

const Dashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const { selectedCategory } = useFilterStore()
  const { data: filterOptions } = useFilterOptions()
  
  // URLパラメータからフィルターを自動設定（YouTube投稿などからの流入時）
  useURLParams()

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen)
  }

  const closeSidebar = () => {
    setIsSidebarOpen(false)
  }

  // 選択されたカテゴリの名前を取得
  const selectedCategoryName = selectedCategory 
    ? filterOptions?.categories?.find(cat => cat.id === selectedCategory)?.name 
    : null

  // タイトルを動的に生成
  const getTitle = () => {
    if (selectedCategoryName) {
      return selectedCategoryName
    }
    return '【ニッチな分析】ダッシュボード'
  }

  // メタディスクリプションを動的に生成
  const getDescription = () => {
    if (selectedCategoryName) {
      return `${selectedCategoryName}のデータ分析とランキング、最新情報をお届けします`
    }
    return '高校サッカーのデータ分析とランキング、最新情報をお届けします'
  }

  // BreadcrumbList構造化データ生成
  const getBreadcrumbData = () => {
    return {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        {
          "@type": "ListItem",
          "position": 1,
          "name": "ホーム",
          "item": "https://niche-football.com"
        },
        {
          "@type": "ListItem",
          "position": 2,
          "name": selectedCategoryName || "ダッシュボード",
          "item": "https://niche-football.com"
        }
      ]
    }
  }

  return (
    <>
      <Helmet>
        <title>{getTitle()}</title>
        <meta name="description" content={getDescription()} />
        <meta property="og:title" content={getTitle()} />
        <meta property="og:description" content={getDescription()} />
        <meta property="twitter:title" content={getTitle()} />
        <meta property="twitter:description" content={getDescription()} />
        
        {/* BreadcrumbList構造化データ */}
        <script type="application/ld+json">
          {JSON.stringify(getBreadcrumbData())}
        </script>
      </Helmet>
      <SWRConfig
        value={{
          fetcher: (url: string) => fetch(url).then((res) => res.json()),
          revalidateOnFocus: false,
        }}
      >
        <div className="min-h-screen bg-gray-50">
        <div className="flex">
          {/* デスクトップサイドバー */}
          <div className="hidden lg:block w-80 bg-white shadow-lg min-h-screen">
            <GlobalFilters />
          </div>
          
                      {/* メインコンテンツ */}
            <div className="flex-1 p-2 sm:p-4 lg:p-6 overflow-hidden">
            <div className="w-full">
              {/* モバイルヘッダー */}
              <div className="lg:hidden flex items-center justify-between mb-4 sm:mb-6">
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex-1 min-w-0">
                  {getTitle()}
                </h1>
                <button
                  onClick={toggleSidebar}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500 flex-shrink-0 ml-2"
                >
                  <Filter className="h-5 w-5 sm:h-6 sm:w-6" />
                </button>
              </div>

              {/* デスクトップタイトル */}
              <h1 className="hidden lg:block text-3xl font-bold text-gray-900 mb-8">
                {getTitle()}
              </h1>
              
              {/* アクティブフィルターバー */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <ActiveFiltersBar />
              </div>
              
              {/* YouTubeバナー（YouTubeからの流入時） */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <YouTubeBanner />
              </div>
              
              {/* デバッグ情報 */}
              {/* <div className="mb-8">
                <DebugInfo />
              </div> */}
              
              {/* サマリーカード */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <SummaryCards />
              </div>
              
              {/* チャートエリア */}
              <div className="grid grid-cols-1 xl:grid-cols-2 gap-2 sm:gap-4 lg:gap-6 mb-4 sm:mb-6 lg:mb-8">
                <div className="bg-white p-2 sm:p-3 lg:p-6 rounded-lg shadow overflow-hidden">
                  <h2 className="text-sm sm:text-lg lg:text-xl font-semibold mb-2 sm:mb-4">都道府県別優勝回数</h2>
                  <PrefectureRankingChart />
                </div>
                
                <div className="bg-white p-2 sm:p-3 lg:p-6 rounded-lg shadow overflow-hidden">
                  <div className="flex flex-col lg:flex-row lg:items-center mb-2 sm:mb-4">
                    <h2 className="text-sm sm:text-lg lg:text-xl font-semibold">高校別勝利数</h2>
                    <span className="mt-1 lg:mt-0 lg:ml-2 text-xs sm:text-sm text-gray-500">※両校優勝、両校３位は勝利扱いでカウント</span>
                  </div>
                  <TeamWinRankingChart />
                </div>
              </div>
              
              {/* ゴール数推移 */}
              <div className="bg-white p-2 sm:p-3 lg:p-6 rounded-lg shadow mb-4 sm:mb-6 lg:mb-8 overflow-hidden">
                <h2 className="text-sm sm:text-lg lg:text-xl font-semibold mb-2 sm:mb-4">大会毎のゴール数推移</h2>
                <GoalTrendChart />
              </div>
              
              {/* 日本地図 */}
              <div className="bg-white p-2 sm:p-3 lg:p-6 rounded-lg shadow mb-4 sm:mb-6 lg:mb-8 overflow-hidden">
                {/* <h2 className="text-xl font-semibold mb-4">優勝回数ヒートマップ</h2> */}
                <ChampionshipMap />
              </div>
              
              {/* 勝率上位10県 */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <TopWinRatePrefectures />
              </div>
              
              {/* 対戦数上位10県 */}
              <div className="mb-4 sm:mb-6 lg:mb-8">
                <TopMatchCountPrefectures />
              </div>
              
              {/* 試合結果テーブル */}
              <div className="bg-white p-2 sm:p-3 lg:p-6 rounded-lg shadow overflow-hidden">
                <h2 className="text-sm sm:text-lg lg:text-xl font-semibold mb-2 sm:mb-4">試合結果一覧</h2>
                <MatchesDataTable />
              </div>
            </div>
          </div>
        </div>

        {/* モバイルサイドバーオーバーレイ */}
        {isSidebarOpen && (
          <div className="lg:hidden fixed inset-0 z-50">
            {/* オーバーレイ背景 */}
            <div 
              className="fixed inset-0 bg-black bg-opacity-50"
              onClick={closeSidebar}
            ></div>
            
            {/* サイドバーパネル */}
            <div className="fixed right-0 top-0 h-full w-80 bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
              <div className="flex items-center justify-between p-4 border-b">
                <h2 className="text-lg font-semibold text-gray-900">フィルター</h2>
                <button
                  onClick={closeSidebar}
                  className="inline-flex items-center justify-center p-2 rounded-md text-gray-700 hover:text-blue-600 hover:bg-blue-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-blue-500"
                >
                  <X className="h-6 w-6" />
                </button>
              </div>
              <div className="overflow-y-auto h-full">
                <GlobalFilters />
              </div>
            </div>
          </div>
        )}
      </div>
    </SWRConfig>
    </>
  )
}

export default Dashboard
